import React from 'react';
import PropTypes from 'prop-types';
import VisitCard from './VisitCard';
import { generateComponentDocs, generateStoryDocs } from '../../utils/documentationUtils';

// Mock Data
const mockVisit = {
  id: 1,
  site_id: 101,
  completed: true,
  site_name: 'Sample Site',
  date: '2024-11-15',
  purpose: 'Routine Visit',
  leader: 'John Doe',
  workers_interacted: 10,
  total_workers: 20,
};

export default {
  title: 'Components/Visits/VisitCard',
  component: VisitCard,
  parameters: {
    layout: 'centered',
    ...generateComponentDocs(
      // What problem does it solve?
      '\nThe `VisitCard` component provides a concise and visually appealing summary of visit details. It is used in the cards/gallery view to display individual visit records.\n\n',
      // When & how to use it?
      '\nUse the `VisitCard` component in a grid/gallery layout when a compact representation of visit details is needed. It is typically used within the `VisitCards` component to display multiple visit records.\n\n',
      // Current Usage
      `\nThe \`VisitCard\` component is used within the \`VisitCards\` component on the VisitsPage. It displays details like:
      \n- Completion Status (Completed/Attempted)
      \n- Site Name
      \n- Visit Date
      \n- Purpose
      \n- Leader
      \n- Workers Reached
      \n- Total Workers\n\n`,
      // Related Components
      `\n- **[VisitCards](http://localhost:6006/?path=/docs/components-visits-visitcards--docs)**: Parent component that renders multiple VisitCard components in a grid layout.
      \n- **[VisitsPage](http://localhost:6006/?path=/docs/pages-visitspage--docs)**: Page where VisitCards component is used within the cards/gallery view.\n\n`,
    ),
  },
  tags: ['autodocs'],
  argTypes: {
    visit: {
      control: { type: 'object' },
      description: 'The visit object containing all the details to be displayed on the card.\n\n',
      table: {
        type: { summary: 'object' },
        required: true,
      },
    },
  },
};

const VisitCardTemplate = ({ visit }) => (
  <div className="grid grid-cols-1 sm:grid-cols-1 md2:grid-cols-2 xl:grid-cols-3 gap-8">
    <VisitCard visit={visit} />
  </div>
);

VisitCardTemplate.propTypes = {
  visit: PropTypes.object.isRequired,
};

const Template = VisitCardTemplate.bind({});

export const DefaultVisitCard = Template.bind({});
DefaultVisitCard.args = {
  visit: mockVisit,
};
DefaultVisitCard.parameters = generateStoryDocs(
  // When is this variant used?
  '\nThis variant is used to display a single `VisitCard` with sample data. It is typically rendered within the `VisitCards` component on the VisitsPage.\n\n',
  // Where is it currently used on the product?
  '\nThe `VisitCard` component is used in the cards/gallery view on the VisitsPage to display individual visit records.\n\n',
);
